import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  Coin,
  CreateCashTransactionGroupTransactionResultDto,
  DeleteCashTransactionGroupLinkDto,
  DeleteCashTransactionGroupLinkResultDto,
  GetCashListNursingHomeConfigurationDto,
  GetCashManagementWithTransactionDto,
  GetCashTransactionGroupListResponseClient,
  GetCashTransactionGroupResultDto,
  GetCashTransactionGroupsDto,
  PostCashManagementDto,
  PostCashTransactionGroupLinkDto,
  PostCashTransactionGroupLinkResultDto,
  PostCashTransactionGroupTransactionDto,
} from '@pflegenavi/shared/api';
import {
  CashManagementTransactionType,
  headerConstants,
} from '@pflegenavi/shared/api';
import { createContext, useCallback } from 'react';
import type {
  Query,
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_DASHBOARD_STATISTICS_KEY, TRANSFER_KEY_ALL } from '../payment';
import { invalidateAllResidentBalance } from '../reporting/queryKeys';
import { invalidateResident } from '../resident/queryKeys';
import {
  getReceiptKey,
  getTransactionsKey,
  TRANSACTIONS_PAGINATED_KEY,
} from '../transaction/queryKeys';
import type { ICashManagementApi } from './api';
import { addMissingFactorsAndSort, CashManagementApi } from './api';
import {
  CASH_LIST_CONFIGURATION_KEY,
  CASH_LIST_TRANSACTIONS_KEY,
  CASH_TRANSACTION_GROUP_KEY,
  CASH_TRANSACTION_GROUP_LIST_KEY,
  invalidateCashList,
  MAKE_CASH_TRANSACTION_GROUP_LIST_KEY,
} from './queryKeys';
import { GET_RECEIPT_KEY } from '../receipt-phoenix/queryKeys';
import {
  CASH_LISTS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
  CASH_LIST_CONFIGURATION_PHOENIX_KEY,
  CASH_LIST_TRANSACTIONS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';

export { addMissingFactorsAndSort } from './api';
export type { ICashManagementApi } from './api';

const ApiContext = createContext<ICashManagementApi | undefined>(undefined);
const { useApi: useCashManagementApi, ApiProvider: CashManagementApiProvider } =
  makeApiProvider({
    name: 'CashManagement',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new CashManagementApi(tenantId, auth, apiUrl),
  });

export {
  ApiContext as CashManagementApiContext,
  CashManagementApiProvider,
  useCashManagementApi,
};

let lastCashTransactionGroupListModificationTimestamp: number | undefined =
  undefined;

export const useGetCashListConfiguration = (
  nursingHomeId: string | undefined
): UseQueryResult<GetCashListNursingHomeConfigurationDto, unknown> => {
  return useApiQuery(
    useCashManagementApi,
    CASH_LIST_CONFIGURATION_KEY(nursingHomeId),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getCashListConfiguration({ params: { nursingHomeId } });
    },
    {
      enabled: nursingHomeId !== undefined,
    }
  );
};

type AddTransaction = Omit<PostCashManagementDto, 'coins'> & {
  coins?: Coin[];
};

interface UseCashManagementAddTransactionOptions {
  /**
   * Runs before the query is invalidated. Can be used to close modals.
   */
  onBeforeInvalidateCashList?: () => void;
  nursingHomeId: string | undefined;
}

export const useCashManagementAddTransaction = (
  opts: UseCashManagementAddTransactionOptions
): UseMutationResult<
  GetCashManagementWithTransactionDto,
  unknown,
  AddTransaction
> => {
  const api = useCashManagementApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    GetCashManagementWithTransactionDto,
    unknown,
    AddTransaction
  >({
    mutationKey: ['CASH_LIST_TRANSACTION_ADD'],
    mutationFn: ({ coins, cashListId, ...data }) => {
      if (!cashListId) {
        throw new Error('cashList is not initialized yet');
      }
      const shouldRemoveCoinsFactors =
        data.bankAccountAmount &&
        data.bankAccountAmount !== 0 &&
        data.type !== CashManagementTransactionType.Adjustment;

      const allCoins = shouldRemoveCoinsFactors
        ? undefined
        : coins
        ? addMissingFactorsAndSort(coins).map((coin) => coin.amount)
        : undefined;
      const body = {
        ...data,
        coins: allCoins,
        cashListId,
      };

      return api
        .addTransaction({ body })
        .then(extractLastModifiedFromResponse)
        .then((r) => r.data);
    },
    onSuccess: async (_result, data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CASH_LIST_TRANSACTIONS_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        ...(data.type === CashManagementTransactionType.StripeTransfer
          ? [
              queryClient.invalidateQueries({ queryKey: TRANSFER_KEY_ALL }),
              queryClient.invalidateQueries({
                queryKey: PAYMENT_DASHBOARD_STATISTICS_KEY,
              }),
            ]
          : []),
        ...(data.resident_id
          ? [
              invalidateAllResidentBalance(queryClient, opts.nursingHomeId),
              invalidateResident(
                queryClient,
                data.resident_id,
                opts.nursingHomeId
              ),
              queryClient.invalidateQueries({
                queryKey: getTransactionsKey(data.resident_id),
              }),
            ]
          : []),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PAGINATED_KEY({
            nursingHomeId: opts.nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
      ]);

      // PNA-1820 Called before invalidating cash list to avoid flickering in the modal
      opts?.onBeforeInvalidateCashList?.();

      await invalidateCashList(queryClient, opts.nursingHomeId);
    },
  });
  return result;
};

export function useCashTransactionGroupAddLink(): UseMutationResult<
  PostCashTransactionGroupLinkResultDto,
  unknown,
  PostCashTransactionGroupLinkDto
> {
  const api = useCashManagementApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    PostCashTransactionGroupLinkResultDto,
    unknown,
    PostCashTransactionGroupLinkDto
  >({
    mutationKey: ['cash-transaction-group-add-link'],
    mutationFn: (data) =>
      api
        .postCashTransactionGroupLink({
          params: {
            cashTransactionGroupId: data.cashTransactionGroupId,
          },
          body: {
            transactionId: data.transactionId,
            receiptId: data.receiptId,
            transactionIds: data.transactionIds,
            receiptIds: data.receiptIds,
          },
        })
        .then(extractLastModifiedFromResponse)
        .then((r) => r.data),
    onSuccess: (_, data) => {
      const additionalInvalidations = [];
      if (data.receiptId) {
        additionalInvalidations.push(
          queryClient.invalidateQueries({
            queryKey: getReceiptKey(data.receiptId),
          }),
          queryClient.invalidateQueries({
            queryKey: GET_RECEIPT_KEY(data.receiptId),
          }),
          queryClient.invalidateQueries({
            queryKey: PLIANT_TRANSACTIONS_KEY(),
          })
        );
      }
      if (data.receiptIds) {
        additionalInvalidations.push(
          ...data.receiptIds.map((receiptId) =>
            queryClient.invalidateQueries({
              queryKey: getReceiptKey(receiptId),
            })
          ),
          ...data.receiptIds.map((receiptId) =>
            queryClient.invalidateQueries({
              queryKey: GET_RECEIPT_KEY(receiptId),
            })
          )
        );
      }

      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_KEY(data.cashTransactionGroupId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            data.cashTransactionGroupId
          ),
        }),
        ...additionalInvalidations,
      ]);
    },
  });
  return result;
}

export function useCashTransactionGroupRemoveLink(): UseMutationResult<
  DeleteCashTransactionGroupLinkResultDto,
  unknown,
  DeleteCashTransactionGroupLinkDto
> {
  const api = useCashManagementApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    DeleteCashTransactionGroupLinkResultDto,
    unknown,
    DeleteCashTransactionGroupLinkDto
  >({
    mutationKey: ['cash-transaction-group-remove-link'],
    mutationFn: (data) =>
      api
        .deleteCashTransactionGroupLink({
          params: {
            cashTransactionGroupId: data.cashTransactionGroupId,
            transactionId: data.transactionId,
            receiptId: data.receiptId,
            transactionIds: data.transactionIds,
            receiptIds: data.receiptIds,
          },
        })
        .then(extractLastModifiedFromResponse)
        .then((r) => r.data),
    onSuccess: (_, data) => {
      const additionalInvalidations = [];
      if (data.receiptId) {
        additionalInvalidations.push(
          queryClient.invalidateQueries({
            queryKey: getReceiptKey(data.receiptId),
          }),
          queryClient.invalidateQueries({
            queryKey: GET_RECEIPT_KEY(data.receiptId),
          })
        );
      }
      if (data.receiptIds) {
        additionalInvalidations.push(
          ...data.receiptIds.map((receiptId) =>
            queryClient.invalidateQueries({
              queryKey: getReceiptKey(receiptId),
            })
          ),
          ...data.receiptIds.map((receiptId) =>
            queryClient.invalidateQueries({
              queryKey: GET_RECEIPT_KEY(receiptId),
            })
          )
        );
      }
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_KEY(data.cashTransactionGroupId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            data.cashTransactionGroupId
          ),
        }),
        ...additionalInvalidations,
      ]);
    },
  });
  return result;
}

const POLLING_INTERVAL = 200;

export function useCashTransactionGroups(
  data: GetCashTransactionGroupsDto,
  options?: Omit<
    UseQueryOptions<GetCashTransactionGroupListResponseClient, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetCashTransactionGroupListResponseClient, unknown> {
  const select = useCallback(
    (data: GetCashTransactionGroupListResponseClient) => {
      const newData = data.data.map((row) => ({
        ...row,
        updateDate: row.updateDate && new Date(row.updateDate),
        semanticCreationDate:
          row.semanticCreationDate && new Date(row.semanticCreationDate),
        cashChanged: row.cashChanged ? row.cashChanged / 100 : row.cashChanged, // Falsy when 0 or undefined. In both cases, we can just use the value directly
        cashChangedCents: row.cashChanged,
        separateFeeAmount: row.separateFeeAmount
          ? row.separateFeeAmount / 100
          : row.separateFeeAmount,
        separateFeeAmountCents: row.separateFeeAmount,
        immediateFeeAmount: row.immediateFeeAmount
          ? row.immediateFeeAmount / 100
          : row.immediateFeeAmount,
        immediateFeeAmountCents: row.immediateFeeAmount,
        amountLinked: row.amountLinked
          ? row.amountLinked / 100
          : row.amountLinked,
        amountLinkedCents: row.amountLinked,
        linkingDifference: row.linkingDifference
          ? row.linkingDifference / 100
          : row.linkingDifference,
        linkingDifferenceCents: row.linkingDifference,
        separateFeeAmountLinked: row.separateFeeAmountLinked
          ? row.separateFeeAmountLinked / 100
          : row.separateFeeAmountLinked,
        separateFeeAmountLinkedCents:
          row.separateFeeAmountLinked ?? row.separateFeeAmountLinked,
        separateFeeAmountLinkingDifference:
          row.separateFeeAmountLinkingDifference
            ? row.separateFeeAmountLinkingDifference / 100
            : row.separateFeeAmountLinkingDifference,
        separateFeeAmountLinkingDifferenceCents:
          row.separateFeeAmountLinkingDifference,
        immediateFeeAmountLinked: row.immediateFeeAmountLinked
          ? row.immediateFeeAmountLinked / 100
          : row.immediateFeeAmountLinked,
        immediateFeeAmountLinkedCents: row.immediateFeeAmountLinked,
        immediateFeeAmountLinkingDifference:
          row.immediateFeeAmountLinkingDifference
            ? row.immediateFeeAmountLinkingDifference / 100
            : row.immediateFeeAmountLinkingDifference,
        immediateFeeAmountLinkingDifferenceCents:
          row.immediateFeeAmountLinkingDifference,
        mismatchConfirmationDate: row.mismatchConfirmationDate
          ? new Date(row.mismatchConfirmationDate)
          : undefined,
      }));
      return {
        ...data,
        data: newData,
      };
    },
    []
  );

  const refetchInterval = useCallback(
    (
      response: Query<
        GetCashTransactionGroupListResponseClient,
        unknown,
        GetCashTransactionGroupListResponseClient,
        QueryKey
      >
    ) => {
      if (
        lastCashTransactionGroupListModificationTimestamp &&
        (response?.state?.data?.meta?.lastRefresh === undefined ||
          response?.state?.data?.meta?.lastRefresh <
            lastCashTransactionGroupListModificationTimestamp)
      ) {
        return POLLING_INTERVAL;
      }

      return false;
    },
    []
  );

  const query = useApiQuery(
    useCashManagementApi,
    MAKE_CASH_TRANSACTION_GROUP_LIST_KEY(data.cashListId, data),
    (api) => {
      return api.getCashTransactionGroups({ ...data });
    },
    {
      ...options,
      select,
      refetchInterval: refetchInterval,
    }
  );

  return query;
}

export function useCashTransactionGroup(
  groupId?: string
): UseQueryResult<GetCashTransactionGroupResultDto, unknown> {
  const select = useCallback((data: GetCashTransactionGroupResultDto) => {
    return {
      ...data,
      updateDate: data.updateDate && new Date(data.updateDate),
      semanticCreationDate:
        data.semanticCreationDate && new Date(data.semanticCreationDate),
      cashChanged: data.cashChanged,
      amountLinked: data.amountLinked,
      transactions: data.transactions?.map((row) => ({
        ...row,
        date: row.date && new Date(row.date),
      })),
      cashListTransactions: data.cashListTransactions?.map((row) => ({
        ...row,
        amount: row.amount,
        date: row.date && new Date(row.date),
        eventDate: row.eventDate && new Date(row.eventDate),
      })),
    };
  }, []);
  return useApiQuery(
    useCashManagementApi,
    CASH_TRANSACTION_GROUP_KEY(groupId ?? ''),
    (api) => {
      if (!groupId) {
        throw new Error('groupId is required');
      }
      return api.getCashTransactionGroup({ params: { groupId } });
    },
    {
      enabled: groupId !== undefined,
      select,
    }
  );
}

export function useCashTransactionGroupAddTransaction(
  nursingHomeId: string | undefined
): UseMutationResult<
  CreateCashTransactionGroupTransactionResultDto,
  unknown,
  PostCashTransactionGroupTransactionDto
> {
  const api = useCashManagementApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    CreateCashTransactionGroupTransactionResultDto,
    unknown,
    PostCashTransactionGroupTransactionDto
  >({
    mutationKey: ['cash-transaction-group-add-transaction'],
    mutationFn: (data) =>
      api
        .postCashTransactionGroupTransaction({
          params: {
            cashTransactionGroupId: data.cashTransactionGroupId,
          },
          body: {
            coins: data.coins,
            type: data.type,
            notes: data.notes,
            bankAccountAmount: data.bankAccountAmount,
          },
        })
        .then(extractLastModifiedFromResponse)
        .then((r) => r.data),
    onSuccess: async (_, props) => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_CONFIGURATION_KEY(nursingHomeId),
        }),
        ...(nursingHomeId
          ? [
              queryClient.invalidateQueries({
                queryKey: CASH_LIST_CONFIGURATION_PHOENIX_KEY(nursingHomeId),
              }),
            ]
          : []),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_KEY(props.cashTransactionGroupId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
            props.cashTransactionGroupId
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
}

function extractLastModifiedFromResponse<
  T extends {
    headers: Headers;
  }
>(response: T) {
  const timestamp = response.headers.get(headerConstants.lastEventHeader);
  if (timestamp) {
    lastCashTransactionGroupListModificationTimestamp = parseInt(timestamp, 10);
  }
  return response;
}

export function useTransferCallback(
  nursingHomeId: string | undefined
): CashManagementApi['transfer'] {
  const query = useQueryClient();
  const api = useCashManagementApi();

  return useCallback(
    async (...args: Parameters<typeof api.transfer>) => {
      const result = await api.transfer(...args);
      await Promise.all([
        query.invalidateQueries({
          queryKey: MAKE_CASH_TRANSACTION_GROUP_LIST_KEY(
            args[0].params.fromCashListId,
            {}
          ),
        }),
        query.invalidateQueries({
          queryKey: MAKE_CASH_TRANSACTION_GROUP_LIST_KEY(
            args[0].params.toCashListId,
            {}
          ),
        }),
        invalidateCashList(query, nursingHomeId),
        query.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        ...(nursingHomeId
          ? [
              query.invalidateQueries({
                queryKey: CASH_LIST_CONFIGURATION_PHOENIX_KEY(nursingHomeId),
              }),
            ]
          : []),
        query.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
      ]);
      return result;
    },
    [api, nursingHomeId, query]
  );
}
